import type { To } from 'react-router-dom';

/**
 * Return a path with locale prefix. If path does not start with '/', then the current path appended with path will be returned.
 * @param pathName The path name
 * @param currentPath The current path
 * @param countryCode The country code
 * @param language The language
 * @returns Returns the path prefixed with locale
 */
export const formatPathWithLocale = (
  pathName: string,
  currentPath: string,
  countryCode: string,
  language: string
) => {
  if (!pathName.startsWith('/')) {
    return `${currentPath}/${pathName}`;
  }

  return `/${countryCode}/${language}${pathName}`;
};

/**
 * Prefixes the path in a To object with locale. If the path does not start with '/', then the current path appended with path will be returned.
 * @param to To navigation object
 * @param currentPath The current path
 * @param countryCode The country code
 * @param language The language
 * @returns Returns a To object with locale prefixed path
 */
export const getPathWithLocale = (
  to: To,
  currentPath: string,
  countryCode: string,
  language: string
): To => {
  if (typeof to === 'string') {
    return formatPathWithLocale(to, currentPath, countryCode, language);
  } else if (typeof to === 'object') {
    const { pathname } = to;

    if (pathname) {
      const path = formatPathWithLocale(pathname, currentPath, countryCode, language);
      return { ...to, pathname: path };
    }
  }

  return to;
};
